import React, { useState, useCallback } from 'react';
import HotelSearch from './components/HotelSearch';
import HotelResults from './components/HotelResults';
import HotelCompare from './components/HotelCompare';
import HotelBooking from './components/HotelBooking';
import Confirmation from './components/Confirmation';
import './App.css';

function App() {
  // View state
  const [view, setView] = useState('search');

  // Search parameters
  const [searchParams, setSearchParams] = useState({
    cityCode: '',
    checkIn: '',
    checkOut: '',
    guests: 1,
    rooms: 1,
    starRating: 'any',
  });

  // Results and selection
  const [hotels, setHotels] = useState([]);
  const [selectedHotels, setSelectedHotels] = useState(new Set());
  const [comparePairings, setComparePairings] = useState([]);

  // Booking
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle search submission
  const handleSearch = useCallback(async (params) => {
    setSearchParams(params);
    setLoading(true);
    setError(null);
    setSelectedHotels(new Set());

    try {
      const response = await fetch('/api/hotel-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      setHotels(data.hotels || []);
      if (data.warnings && data.warnings.length > 0 && data.hotels.length === 0) {
        setError(`Sabre returned ${data.maxResults || 0} potential results but no hotel details. API warning: ${data.warnings.join('; ')}. This may be a credentials/PCC authorization issue.`);
      }
      setView('results');
    } catch (err) {
      setError(err.message || 'Failed to search hotels');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle hotel selection for comparison
  const handleSelectHotel = useCallback((hotelId) => {
    setSelectedHotels((prev) => {
      const next = new Set(prev);
      if (next.has(hotelId)) {
        next.delete(hotelId);
      } else {
        next.add(hotelId);
      }
      return next;
    });
  }, []);

  // Handle select all in results
  const handleSelectAll = useCallback((hotelIds) => {
    setSelectedHotels(new Set(hotelIds));
  }, []);

  // Handle deselect all in results
  const handleDeselectAll = useCallback(() => {
    setSelectedHotels(new Set());
  }, []);

  // Handle build comparative quote
  const handleBuildComparison = useCallback(() => {
    const selected = hotels.filter((h) => selectedHotels.has(h.id));
    setComparePairings(selected);
    setView('compare');
  }, [hotels, selectedHotels]);

  // Handle select for booking from comparison
  const handleSelectForBooking = useCallback((hotel) => {
    setSelectedBooking(hotel);
    setView('booking');
  }, []);

  // Handle booking submission
  const handleSubmitBooking = useCallback(async (guestDetails) => {
    setLoading(true);
    setError(null);

    try {
      const bookingData = {
        hotelId: selectedBooking.id,
        roomType: selectedBooking.selectedRoom?.type,
        guestDetails,
        checkIn: searchParams.checkIn,
        checkOut: searchParams.checkOut,
      };

      const response = await fetch('/api/hotel-book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error(`Booking failed: ${response.statusText}`);
      }

      const data = await response.json();
      setBookingConfirmation(data);
      setView('confirmation');
    } catch (err) {
      setError(err.message || 'Failed to complete booking');
      console.error('Booking error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedBooking, searchParams]);

  // Navigation handlers
  const goToSearch = useCallback(() => {
    setView('search');
    setSelectedHotels(new Set());
    setComparePairings([]);
    setSelectedBooking(null);
    setError(null);
  }, []);

  const goToResults = useCallback(() => {
    setView('results');
    setSelectedBooking(null);
    setComparePairings([]);
  }, []);

  const goToComparison = useCallback(() => {
    if (selectedHotels.size >= 2) {
      handleBuildComparison();
    }
  }, [selectedHotels.size, handleBuildComparison]);

  // Render current view
  const renderView = () => {
    switch (view) {
      case 'search':
        return <HotelSearch onSearch={handleSearch} loading={loading} />;

      case 'results':
        return (
          <HotelResults
            hotels={hotels}
            selectedHotels={selectedHotels}
            onSelectHotel={handleSelectHotel}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBuildComparison={handleBuildComparison}
            searchParams={searchParams}
            loading={loading}
            onBackToSearch={goToSearch}
          />
        );

      case 'compare':
        return (
          <HotelCompare
            hotels={comparePairings}
            searchParams={searchParams}
            onSelectForBooking={handleSelectForBooking}
            onBackToResults={goToResults}
            loading={loading}
          />
        );

      case 'booking':
        return (
          <HotelBooking
            hotel={selectedBooking}
            searchParams={searchParams}
            onSubmit={handleSubmitBooking}
            onCancel={goToComparison}
            loading={loading}
          />
        );

      case 'confirmation':
        return (
          <Confirmation
            confirmation={bookingConfirmation}
            selectedBooking={selectedBooking}
            searchParams={searchParams}
            onStartNewSearch={goToSearch}
          />
        );

      default:
        return <HotelSearch onSearch={handleSearch} loading={loading} />;
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-brand">
            <h1>🏨 HotelDesk</h1>
          </div>
          {view !== 'search' && (
            <nav className="header-nav">
              <button
                className="nav-button"
                onClick={goToSearch}
                title="Start new search"
              >
                ← New Search
              </button>
            </nav>
          )}
        </div>
      </header>

      <main className="app-main">
        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠</span>
            <span>{error}</span>
            <button
              className="error-dismiss"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}
        {renderView()}
      </main>
    </div>
  );
}

export default App;
